import { useContext } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from '@node-space/ui-components'
import { logSentryError } from '@node-space/utils'
import { yupFormResolver } from 'hooks/forms/yupFormResolver'
import { useEditChannel } from 'hooks/mutations/useEditChannel'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import { BaseErrorResponse } from 'types/beneficiaries'
import { channelEditSchema, type ChannelEditFormFields } from '../schemas/channelEditSchema'

export const useChannelEditForm = () => {
  const { t } = useTranslation()
  const [walletAction] = useContext(WalletActionContext)
  const sourceChannel = walletAction?.meta?.sourceChannel
  const onChannelEditSuccess = walletAction?.meta?.onChannelEditSuccess

  const queryClient = useQueryClient()
  const { mutate, isPending } = useEditChannel()

  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()

  const form = useForm<ChannelEditFormFields>({
    defaultValues: {
      channelDescription: sourceChannel.reference,
    },
    resolver: yupFormResolver<ChannelEditFormFields>(channelEditSchema(t)),
    mode: 'onChange',
  })

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
  } = form

  const onSubmit: SubmitHandler<ChannelEditFormFields> = value => {
    resetRequestError()

    const payload = {
      uuid: sourceChannel?.uuid,
      merchantId: sourceChannel?.merchantId,
      displayCurrency: sourceChannel?.displayCurrency,
      payCurrency: sourceChannel?.payCurrency,
      reference: value.channelDescription,
    }

    mutate(payload, {
      onSuccess: async () => {
        toast({ header: t('payments.channels.updateSuccess'), variant: 'success' })

        queryClient.invalidateQueries({ queryKey: [reactQueryName.CHANNEL] })
        onChannelEditSuccess?.()
        walletAction?.onClose()
      },
      onError: (error: BaseErrorResponse) => {
        const errorList = error?.errorList || error?.data?.errorList || []
        const hasChannelWithPaymentsError = errorList.some(
          err =>
            err.message ===
            'Cannot update reference for channel with payments. Please create a new channel.'
        )

        setRequestError({
          show: true,
          errorCode: error?.status,
          ...(hasChannelWithPaymentsError && {
            handledMessage: t('payments.channels.updateErrorChannelWithPayments'),
          }),
        })
        logSentryError('Error: useEditChannel', error)
      },
    })
  }

  return {
    register,
    handleSubmit,
    onSubmit,
    errors,
    isDirty,
    isValid,
    isPending,
    requestError,
    walletAction,
    t,
  }
}
